// src/services/jobs.js
import { db } from './firebase';
import { 
    collection, 
    getDocs, 
    query, 
    orderBy, 
    doc, 
    updateDoc, 
    getDoc, 
    addDoc,
    deleteDoc,
    where,
    limit,
    startAfter,
    arrayUnion,
    arrayRemove
} from "firebase/firestore";

// Get all jobs
export const getAllJobs = async () => {
    try {
        const jobsRef = collection(db, "jobs");
        const q = query(jobsRef, orderBy("closingDate", "asc"));
        const querySnapshot = await getDocs(q);
        
        const jobs = [];
        querySnapshot.forEach((doc) => {
            jobs.push({ id: doc.id, ...doc.data() });
        });
        
        return jobs;
    } catch (error) {
        console.error("Error loading jobs:", error);
        return [];
    }
};

// Get jobs by recruiter
export const getJobsByRecruiter = async (recruiterId) => {
    try {
        const jobsRef = collection(db, "jobs");
        const q = query(jobsRef, where("postedBy", "==", recruiterId));
        const querySnapshot = await getDocs(q);
        
        const jobs = [];
        querySnapshot.forEach((doc) => {
            jobs.push({ id: doc.id, ...doc.data() });
        });
        
        return jobs;
    } catch (error) {
        console.error("Error loading recruiter jobs:", error);
        return [];
    }
};

// Get job by ID
export const getJobById = async (jobId) => {
    try {
        const jobDoc = await getDoc(doc(db, "jobs", jobId));
        if (jobDoc.exists()) {
            return { id: jobDoc.id, ...jobDoc.data() };
        }
        return null;
    } catch (error) {
        console.error("Error loading job:", error);
        return null;
    }
};

// Add job with city and experienceLevel fields
export const addJob = async (jobData) => {
    try {
        const docRef = await addDoc(collection(db, "jobs"), {
            ...jobData,
            city: jobData.city || '',
            experienceLevel: jobData.experienceLevel || '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            applicationCount: 0,
            applications: []
        });
        return { success: true, id: docRef.id };
    } catch (error) {
        console.error("Error adding job:", error);
        return { success: false, error: error.message };
    }
};

// Update job
export const updateJob = async (jobId, jobData) => {
    try {
        const jobRef = doc(db, "jobs", jobId);
        await updateDoc(jobRef, {
            ...jobData,
            city: jobData.city || '',
            experienceLevel: jobData.experienceLevel || '',
            updatedAt: new Date().toISOString()
        });
        return { success: true };
    } catch (error) {
        console.error("Error updating job:", error);
        return { success: false, error: error.message };
    }
};

// Delete job
export const deleteJob = async (jobId) => {
    try {
        await deleteDoc(doc(db, "jobs", jobId));
        return { success: true };
    } catch (error) {
        console.error("Error deleting job:", error);
        return { success: false, error: error.message };
    }
};

// Track application
export const trackApplication = async (userId, jobId) => {
    try {
        const userRef = doc(db, "users", userId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
            const appliedJobs = userDoc.data().appliedJobs || [];
            if (!appliedJobs.includes(jobId)) {
                await updateDoc(userRef, {
                    appliedJobs: [...appliedJobs, jobId]
                });
                
                // Increment application count on job
                const jobRef = doc(db, "jobs", jobId);
                const jobDoc = await getDoc(jobRef);
                if (jobDoc.exists()) {
                    const currentCount = jobDoc.data().applicationCount || 0;
                    const applications = jobDoc.data().applications || [];
                    await updateDoc(jobRef, {
                        applicationCount: currentCount + 1,
                        applications: [...applications, userId]
                    });
                }
            }
        }
        
        return { success: true };
    } catch (error) {
        console.error("Error tracking application:", error);
        return { success: false, error: error.message };
    }
};

// Check if user applied to job
export const hasApplied = async (userId, jobId) => {
    try {
        const userDoc = await getDoc(doc(db, "users", userId));
        if (userDoc.exists()) {
            const appliedJobs = userDoc.data().appliedJobs || [];
            return appliedJobs.includes(jobId);
        }
        return false;
    } catch (error) {
        console.error("Error checking application:", error);
        return false;
    }
};

// Get jobs by province
export const getJobsByProvince = async (province) => {
    try {
        const jobsRef = collection(db, "jobs");
        const q = query(jobsRef, where("province", "==", province));
        const querySnapshot = await getDocs(q);
        
        const jobs = [];
        querySnapshot.forEach((doc) => {
            jobs.push({ id: doc.id, ...doc.data() });
        });
        
        return jobs;
    } catch (error) {
        console.error("Error loading jobs by province:", error);
        return [];
    }
};

// Get jobs by city
export const getJobsByCity = async (city) => {
    try {
        const jobsRef = collection(db, "jobs");
        const q = query(jobsRef, where("city", "==", city));
        const querySnapshot = await getDocs(q);
        
        const jobs = [];
        querySnapshot.forEach((doc) => {
            jobs.push({ id: doc.id, ...doc.data() });
        });
        
        return jobs;
    } catch (error) {
        console.error("Error loading jobs by city:", error);
        return [];
    }
};

// Get jobs by experience level
export const getJobsByExperienceLevel = async (experienceLevel) => {
    try {
        const jobsRef = collection(db, "jobs");
        const q = query(jobsRef, where("experienceLevel", "==", experienceLevel));
        const querySnapshot = await getDocs(q);
        
        const jobs = [];
        querySnapshot.forEach((doc) => {
            jobs.push({ id: doc.id, ...doc.data() });
        });
        
        return jobs;
    } catch (error) {
        console.error("Error loading jobs by experience level:", error);
        return [];
    }
};

// Get featured/sponsored jobs
export const getSponsoredJobs = async () => {
    try {
        const jobsRef = collection(db, "jobs");
        const q = query(
            jobsRef, 
            where("isSponsored", "==", true),
            where("sponsoredUntil", ">", new Date().toISOString())
        );
        const querySnapshot = await getDocs(q);
        
        const jobs = [];
        querySnapshot.forEach((doc) => {
            jobs.push({ id: doc.id, ...doc.data() });
        });
        
        return jobs;
    } catch (error) {
        console.error("Error loading sponsored jobs:", error);
        return [];
    }
};

// Get jobs with pagination
export const getJobsPaginated = async (pageSize = 10, lastDoc = null) => {
    try {
        const jobsRef = collection(db, "jobs");
        let q;
        
        if (lastDoc) {
            q = query(
                jobsRef,
                orderBy("closingDate", "asc"),
                startAfter(lastDoc),
                limit(pageSize)
            );
        } else {
            q = query(
                jobsRef,
                orderBy("closingDate", "asc"),
                limit(pageSize)
            );
        }
        
        const querySnapshot = await getDocs(q);
        const jobs = [];
        let lastVisible = null;
        
        querySnapshot.forEach((doc) => {
            jobs.push({ id: doc.id, ...doc.data() });
            lastVisible = doc;
        });
        
        return { jobs, lastVisible };
    } catch (error) {
        console.error("Error loading paginated jobs:", error);
        return { jobs: [], lastVisible: null };
    }
};